import { can as canRole, effectiveRole, type Capability, type ProjectRole, type Role } from '@one-deployment/shared'
import type { Me, MfaFactor } from '@one-deployment/api'

/**
 * Role gating in the UI (03 §7). The Worker enforces every permission; this
 * only hides what would fail. Same matrix as the Worker (`@one-deployment/shared`), so
 * the two cannot drift.
 */
export function roleOn(me: Me | null | undefined, project?: string | null): Role | null {
  if (!me) return null
  if (!project) return me.globalRole
  const grant = me.projects.find((p) => p.key === project)?.role
  return effectiveRole(me.globalRole, grant === 'admin' ? 'lead' : (grant as ProjectRole | undefined))
}

export function can(me: Me | null | undefined, capability: Capability, project?: string | null): boolean {
  // Global capabilities (people, servers, create project) are admin-only and
  // never granted by a project role.
  if (!project && GLOBAL_ONLY.has(capability)) return me?.globalRole === 'admin'
  return canRole(roleOn(me, project), capability)
}

const GLOBAL_ONLY = new Set<Capability>([
  'projects.create',
  'projects.archive',
  'people.invite',
  'people.roles',
  'people.reset2fa',
  'servers.manage',
  'integrations.manage',
  'claude_accounts.manage',
  'backups.restore',
])

/** Terminal sockets are minted read-only for anyone below dev (03 §7). */
export function terminalMode(me: Me | null | undefined, project: string): 'ro' | 'rw' {
  return can(me, 'terminal.write', project) ? 'rw' : 'ro'
}

/**
 * Which factor the 2FA screen asks for (decision 15b): TOTP when it is
 * enrolled, else a passkey, else none — and none means enrolment.
 */
export function challengeFor(me: Me): MfaFactor | null {
  if (me.factors.totp) return 'totp'
  if (me.factors.passkeys > 0) return 'passkey'
  return null
}
